import { isPlatformBrowser, NgClass } from '@angular/common';
import { Component, effect, inject, LOCALE_ID, OnInit, PLATFORM_ID, signal } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { PharamcyNickname } from './pharamcy-nickname/pharamcy-nickname';
import { PharamcistName } from './pharamcist-name/pharamcist-name';
import { TaxIdentificationNumber } from './tax-identification-number/tax-identification-number';
import { PharmacistNationalId } from './pharmacist-national-id/pharmacist-national-id';
import { CommercialRegisterationNumber } from './commercial-registeration-number/commercial-registeration-number';
import { PharmacyAddress } from './pharmacy-address/pharmacy-address';
import { Governate } from './pharmacyAddress/governate/governate';
import { City } from './pharmacyAddress/city/city';
import { Region } from './pharmacyAddress/region/region';
import { PharmacySegment } from './pharmacy-segment/pharmacy-segment';
import { PharmacyQuota } from './pharmacy-quota/pharmacy-quota';
import { CommercialRegestrationImage } from './pharmacyFileMgt/commercial-regestration-image/commercial-regestration-image';
import { TaxImage } from './pharmacyFileMgt/tax-image/tax-image';
import { PharmacyContractImage } from './pharmacyFileMgt/pharmacy-contract-image/pharmacy-contract-image';
import { NationalIdCard } from './pharmacyFileMgt/national-id-card/national-id-card';
import { PharmacyLicenseImage } from './pharmacyFileMgt/pharmacy-license-image/pharmacy-license-image';
import { AssignRepresentative } from './assign-representative/assign-representative';
import { finalize } from 'rxjs';
import { CustomValidator } from './pharmacy-quota/utils/credit-limit.validator';
import { Error } from '../../public-user-component/auth/shared/error/error';
import { LookupService } from '../../../services/pharmacy/lookup.service';
import { PharmacyService } from '../../../services/pharmacy/pharmacy.service';
import { AuthService } from '../../../services/auth-service';
import { PharmacyDTO } from '../../../services/pharmacy/pharmacy.model';
import { PharmacyLocation } from './pharmacy-location/pharmacy-location';

@Component({
  selector: 'app-create-pharmacy-component',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    PharamcyNickname,
    PharamcistName,
    TaxIdentificationNumber,
    PharmacistNationalId,
    CommercialRegisterationNumber,
    PharmacyAddress,
    Governate,
    City,
    Region,
    PharmacySegment,
    PharmacyQuota,
    CommercialRegestrationImage,
    TaxImage,
    PharmacyContractImage,
    NationalIdCard,
    PharmacyLicenseImage,
    AssignRepresentative,
    Error,
    NgClass,
    PharmacyLocation,
  ],
  templateUrl: './create-pharmacy-component.html',
  styleUrl: './create-pharmacy-component.scss',
})
export class CreatePharmacyComponent implements OnInit {
  private locale = inject(LOCALE_ID);
  private router = inject(Router);
  // private authService = inject(AuthService);
  private lookupService = inject(LookupService);
  private pharmacyService = inject(PharmacyService);
  private platformId = inject(PLATFORM_ID);

  error = signal<string>('');
  governorates = signal<{ id: number; titleEn: string; titleAr?: string; profileImageFid?: string }[]>([]);
  governorateCities = signal<{ id: number; titleEn: string; titleAr?: string; profileImageFid?: string }[]>([]);
  citiesRegions = signal<{ id: number; titleEn: string; titleAr?: string; profileImageFid?: string }[]>([]);
  // AUTO COMPLETE
  fullNameResults = signal<{ id: number; fullName: string }[]>([]);
  showFullNameDropdown = signal(false);
  fullNameLoading = signal(false);
  fullNameSearchTimeout = signal<ReturnType<typeof setTimeout> | null>(null);
  filters = signal<{ fullName: string }>({
    fullName: '',
  });

  // AUTO COMPLETE STATES
  loadingPharmacist = signal<boolean>(false);
  errorPharmacist = signal<boolean>(false);

  // ASSIGN REPRESENTATIVE - AUTO COMPLETE
  representativeResults = signal<{ id: number; fullName: string }[]>([]);
  showRepresentativeDropdown = signal(false);
  loadingRepresentative = signal<boolean>(false);
  errorRepresentative = signal<boolean>(false);
  representativeFilters = signal<{ fullName: string }>({ fullName: '' });

  formSubmitted = signal<boolean>(false);
  loadingCreatePharmacy = signal<boolean>(false);
  errorCreatePharamcy = signal<string>('');

  currentStep = signal<number>(1);

  step1Fields = [
    'nickname',
    'pharmacist',
    'taxIdentificationNumber',
    'pharmacistNationalId',
    'commercialRegisterNumber',
    'commercialRegistrationFid',
    'taxRegistrationCertificateFid',
    'pharmacyContractFid',
    'pharmacistNationalIdFid',
    'pharmacyLicenseFid',
  ];

  step2Fields = ['pharmacyAddress', 'pharmacyGovernorate', 'pharmacyCity', 'pharmacyRegion', 'pharmacyLocation'];
  step3Fields = ['pharmacySegment', 'pharmacyCredit', 'representative'];

  constructor() {
    effect(() => {
      if (isPlatformBrowser(this.platformId)) {
        localStorage.setItem('pharmacyWizardStep', this.currentStep().toString());
      }
    });
  }

  isArabic(): boolean {
    return this.locale.startsWith('ar');
  }
  isEnglish(): boolean {
    return this.locale.startsWith('en');
  }

  onCancel() {
    this.clearDraft();
    this.router.navigate(['/admin/pharmacies/']);
  }
  pharmacyForm = new FormGroup({
    nickname: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(255),
        Validators.pattern(/^[A-Za-z0-9\u0600-\u06FF](?:[A-Za-z0-9\u0600-\u06FF -]*[A-Za-z0-9\u0600-\u06FF])?$/),
      ],
    }),
    pharmacist: new FormControl('', {
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(255)],
    }),
    // TODO: confirm control name matches PharmacyDTO's representative field
    representative: new FormControl('', {
      validators: [Validators.required],
    }),
    taxIdentificationNumber: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(9),
        Validators.maxLength(9),
        Validators.pattern(/^[0-9]+$/),
      ],
    }),
    pharmacistNationalId: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(14),
        Validators.maxLength(14),
        Validators.pattern(/^[0-9]+$/),
      ],
    }),
    commercialRegisterNumber: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(20),
        Validators.pattern(/^(?! )[A-Za-z0-9\u0600-\u06FF\/\\-]+(?<! )$/),
      ],
    }),

    pharmacyAddress: new FormControl('', {
      validators: [Validators.required, Validators.minLength(10), Validators.maxLength(1000)],
    }),
    pharmacyGovernorate: new FormControl('', {
      validators: [Validators.required],
    }),
    pharmacyCity: new FormControl('', {
      validators: [Validators.required],
    }),
    pharmacyRegion: new FormControl('', {
      validators: [Validators.required],
    }),
    pharmacyLocation: new FormControl('', {
      validators: [Validators.required, Validators.maxLength(500), this.urlValidator],
    }),
    pharmacySegment: new FormControl('', {
      validators: [Validators.required],
    }),
    pharmacyCredit: new FormControl('', {
      validators: [Validators.required, Validators.min(0), CustomValidator.creditLimit()],
    }),
    commercialRegistrationFid: new FormControl('', {
      validators: [Validators.required],
    }),
    taxRegistrationCertificateFid: new FormControl('', {
      validators: [Validators.required],
    }),
    pharmacyContractFid: new FormControl('', {
      validators: [Validators.required],
    }),
    pharmacistNationalIdFid: new FormControl('', {
      validators: [Validators.required],
    }),
    pharmacyLicenseFid: new FormControl('', {
      validators: [Validators.required],
    }),
  });

  ngOnInit(): void {
    this.pharmacyForm.get('pharmacist')?.valueChanges.subscribe((value) => {
      this.onFullNameChange(value);
    });

    this.pharmacyForm.get('representative')?.valueChanges.subscribe((value) => {
      this.onRepresentativeChange(value);
    });

    this.lookupService.getGovernorates().subscribe({
      next: (res) => {
        console.log('res: ', res);
        this.governorates.set(res.list);
      },
      error: () => {},
    });

    this.restoreDraft();

    this.pharmacyForm.valueChanges.subscribe((value) => {
      localStorage.setItem('pharmacyWizardData', JSON.stringify(this.pharmacyForm.getRawValue()));
    });
  }

  onGetCities(event: { governorateId: number }): void {
    const { governorateId } = event;
    this.lookupService.getCities([Number(governorateId)]).subscribe({
      next: (res) => {
        this.governorateCities.set(res.list);
      },
      error: () => {},
    });
  }

  onGetRegions(event: { cityId: number }) {
    console.log('region changed: ', event);
    const { cityId } = event;
    this.lookupService.getRegions([Number(cityId)]).subscribe({
      next: (res) => {
        console.log('res regions: ', res);
        this.citiesRegions.set(res.list);
      },
      error: () => {},
    });
  }

  onFullNameChange(value: string | null) {
    if (!value) {
      this.fullNameResults.set([]);
      this.showFullNameDropdown.set(false);
      return;
    }
    this.filters.set({
      fullName: value,
    });
    this.loadingPharmacist.set(true);
    this.errorPharmacist.set(false);

    this.pharmacyService
      .pharmacyOwnerAutoComplete(this.filters().fullName)
      .pipe(
        finalize(() => {
          this.loadingPharmacist.set(false);
        }),
      )
      .subscribe({
        next: (res) => {
          this.showFullNameDropdown.set(true);
          this.fullNameResults.set(res.list);
        },
        error: (err) => {
          this.errorPharmacist.set(true);
        },
      });
  }

  onFullNameFocus() {
    // if (this.filters.fullName && !this.fullNameResults.length) {
    //   this.searchFullNames(this.filters.fullName);
    // }
    // if (this.filters.fullName) this.showFullNameDropdown = true;
  }

  selectFullName(event: { id: string; fullName: string }) {
    const { id, fullName } = event;
    this.pharmacyForm.patchValue(
      {
        pharmacist: id,
      },
      { emitEvent: false },
    );
    this.showFullNameDropdown.set(false);
  }

  onRepresentativeChange(value: string | null) {
    if (!value) {
      this.representativeResults.set([]);
      this.showRepresentativeDropdown.set(false);
      return;
    }
    this.representativeFilters.set({ fullName: value });
    this.loadingRepresentative.set(true);
    this.errorRepresentative.set(false);

    this.pharmacyService
      .pharmacyRepresentativeAutoComplete(this.representativeFilters().fullName)
      .pipe(
        finalize(() => {
          this.loadingRepresentative.set(false);
        }),
      )
      .subscribe({
        next: (res) => {
          this.showRepresentativeDropdown.set(true);
          this.representativeResults.set(res.list);
        },
        error: () => {
          this.errorRepresentative.set(true);
        },
      });
  }

  selectRepresentative(event: { id: string; fullName: string }) {
    const { id } = event;
    this.pharmacyForm.patchValue({ representative: id }, { emitEvent: false });
    this.showRepresentativeDropdown.set(false);
  }

  private searchFullNames(term: string) {}

  onFileUploaded(fid: string, controlName: string) {
    this.pharmacyForm.patchValue({ [controlName]: fid });
  }

  onSubmit() {
    this.formSubmitted.set(true);

    if (!this.isStepValid(this.step3Fields) || this.pharmacyForm.invalid) {
      this.pharmacyForm.markAllAsTouched();
      return;
    }

    const rawValues = this.pharmacyForm.getRawValue();

    const payload: PharmacyDTO = {
      nickName: rawValues.nickname!,
      pharmacyOwnerId: rawValues.pharmacist!,
      representativeId: rawValues.representative!,
      commercialRegistrationNumber: rawValues.commercialRegisterNumber!,
      taxIdentificationNumber: rawValues.taxIdentificationNumber!,
      pharmacyOwnerNationalId: rawValues.pharmacistNationalId!,
      address: rawValues.pharmacyAddress!,
      location: rawValues.pharmacyLocation!,
      pharmacySegmentId: rawValues.pharmacySegment!,
      governorateId: rawValues.pharmacyGovernorate!,
      cityId: rawValues.pharmacyCity!,
      regionId: rawValues.pharmacyRegion!,
      pharmacyCredit: rawValues.pharmacyCredit!,
      commercialRegistrationFid: rawValues.commercialRegistrationFid!,
      taxRegistrationCertificateFid: rawValues.taxRegistrationCertificateFid!,
      pharmacyContractFid: rawValues.pharmacyContractFid!,
      pharmacyLicenseFid: rawValues.pharmacyLicenseFid!,
      pharmacistNationalIdFid: rawValues.pharmacistNationalIdFid!,
    };

    this.errorCreatePharamcy.set('');
    this.loadingCreatePharmacy.set(true);

    this.pharmacyService
      .createPharmacy(payload)
      .pipe(finalize(() => this.loadingCreatePharmacy.set(false)))
      .subscribe({
        next: () => {
          this.clearDraft();
          this.router.navigate(['/admin/pharmacies']);
        },
        error: (err) => {
          this.errorCreatePharamcy.set(
            this.isArabic() ? err.messageAr || 'حدث بعض الخطأ' : err.messageEn || 'some error occured!',
          );
        },
      });
  }

  private restoreDraft() {
    if (!isPlatformBrowser(this.platformId)) return;

    const savedData = localStorage.getItem('pharmacyWizardData');
    if (savedData) {
      try {
        this.pharmacyForm.patchValue(JSON.parse(savedData), { emitEvent: false });
      } catch (e) {
        console.error('Error parsing draft data');
      }
    }

    const savedStep = localStorage.getItem('pharmacyWizardStep');
    if (savedStep && !isNaN(Number(savedStep))) {
      this.currentStep.set(Number(savedStep));
    }
  }

  private clearDraft() {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('pharmacyWizardData');
      localStorage.removeItem('pharmacyWizardStep');
    }
  }

  private isStepValid(stepFields: string[]): boolean {
    let isValid = true;
    stepFields.forEach((field) => {
      const control = this.pharmacyForm.get(field);
      if (control && control.invalid) {
        control.markAsTouched();
        isValid = false;
      }
    });
    return isValid;
  }

  nextStep() {
    let canProceed = false;

    if (this.currentStep() === 1) {
      canProceed = this.isStepValid(this.step1Fields);
    } else if (this.currentStep() === 2) {
      canProceed = this.isStepValid(this.step2Fields);
    }

    if (canProceed) {
      this.currentStep.update((s) => s + 1);
    }
  }

  goToStep(step: number) {
    if (step < this.currentStep()) {
      this.currentStep.set(step);
    } else if (step === this.currentStep() + 1) {
      this.nextStep();
    }
  }

  urlValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    try {
      const url = new URL(control.value);
      return url.protocol === 'http:' || url.protocol === 'https:' ? null : { invalidUrl: true };
    } catch {
      return { invalidUrl: true };
    }
  }
}
